import React, { useState } from "react";
import { Brain, CircleCheck, CircleX, MessageSquare, Play, Terminal, Wrench, FileText, TriangleAlert } from "lucide-react";
import type { TraceEvent } from "../shared/types";
import { cx } from "./ui";

function toolSummary(tool: string, input: any): string {
  if (!input) return "";
  if (input.command) return String(input.command);
  if (input.file_path) return String(input.file_path).replace(/^.*\/ws\//, "");
  if (input.path) return String(input.path);
  if (input.skill) return String(input.skill);
  if (input.pattern) return String(input.pattern);
  if (input.changes) return input.changes.map((c: any) => c.path).join(", ");
  const s = JSON.stringify(input);
  return s.length > 120 ? s.slice(0, 120) + "…" : s;
}

export function TraceView({ events, live }: { events: TraceEvent[]; live?: boolean }) {
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const results = new Map(events.filter((e) => e.type === "tool_result").map((e) => [e.id, e]));
  const items = events.filter((e) => e.type !== "tool_result");
  return (
    <div className="trace">
      {items.map((e, i) => {
        const res = e.type === "tool_call" ? results.get(e.id) : undefined;
        let icon = <Play size={12} />;
        let cls = "";
        let title: React.ReactNode = "";
        let body: React.ReactNode = null;
        if (e.type === "init") {
          title = <span className="muted">{e.text}</span>;
        } else if (e.type === "text") {
          icon = <MessageSquare size={12} />;
          cls = "text";
          title = "Mensagem";
          body = <div style={{ whiteSpace: "pre-wrap" }}>{e.text}</div>;
        } else if (e.type === "thinking") {
          icon = <Brain size={12} />;
          title = <span className="muted">Raciocínio</span>;
          body = open[i] ? <div style={{ whiteSpace: "pre-wrap" }} className="faint">{e.text}</div> : <span className="faint" style={{ cursor: "pointer" }} onClick={() => setOpen({ ...open, [i]: true })}>{(e.text ?? "").slice(0, 110)}…</span>;
        } else if (e.type === "tool_call") {
          icon = e.tool === "Bash" || e.tool === "shell" ? <Terminal size={12} /> : /Read|Write|Edit/.test(e.tool ?? "") ? <FileText size={12} /> : <Wrench size={12} />;
          cls = res?.isError ? "err" : "tool";
          title = (
            <span className="row" style={{ gap: 8, minWidth: 0 }}>
              <span className="tool-name">{e.tool}</span>
              <span className="faint mono ellipsis" style={{ fontSize: 11.5 }}>{toolSummary(e.tool ?? "", e.input)}</span>
            </span>
          );
          body = (
            <>
              {open[i] && e.input && <pre>{JSON.stringify(e.input, null, 2)}</pre>}
              {res?.output && (open[i] || res.output.length < 300) && <pre style={res.isError ? { color: "var(--red)" } : undefined}>{res.output}</pre>}
              {(!open[i] && (res?.output?.length ?? 0) >= 300) || (!open[i] && e.input) ? (
                <span className="faint" style={{ cursor: "pointer", fontSize: 12 }} onClick={() => setOpen({ ...open, [i]: true })}>ver detalhes</span>
              ) : null}
            </>
          );
        } else if (e.type === "result") {
          icon = <CircleCheck size={12} />;
          cls = "ok";
          title = "Concluído";
        } else if (e.type === "error") {
          icon = <CircleX size={12} />;
          cls = "err";
          title = <span style={{ color: "var(--red)" }}>{e.text}</span>;
        } else if (e.type === "log") {
          icon = <TriangleAlert size={12} />;
          title = <span className="faint">{e.text}</span>;
        }
        return (
          <div key={i} className="trace-item">
            <span className={cx("trace-icon", cls)}>{icon}</span>
            <div style={{ minWidth: 0 }}>
              <div className="trace-title">{title}</div>
              {body && <div className="trace-body">{body}</div>}
            </div>
            <span className="trace-t">{(e.t / 1000).toFixed(1)}s</span>
          </div>
        );
      })}
      {live && (
        <div className="trace-item">
          <span className="trace-icon"><span className="spinner" style={{ width: 11, height: 11 }} /></span>
          <div className="trace-title faint">trabalhando…</div>
          <span />
        </div>
      )}
    </div>
  );
}
